import styled from 'styled-components';
import * as React from 'react';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { PeriodType } from '../../containers/Stats/Models';
import { MoodReport, moodTypeFromValue } from '../../containers/Stats/Logic/MoodStatsLogic';
import { WatsonStatsBlockStyled } from '../../containers/Stats/Components/Common';
import { analysisStatusDescriptionMessage, isAnalysisAnalyzed, isAnalysisFailed } from '../../Models';

const messageBase = 'containers.Insights.Mood';

const MoodStatsContainer = styled(WatsonStatsBlockStyled)`
  flex: 1;
`;

interface Props {
  readonly values: ReadonlyArray<MoodReport>;
  readonly periodType: PeriodType;
}

interface BarProps {
  value?: number;
  periodName: string;
}

const Bars = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  overflow-x: scroll;
  height: 100%;
`;

const BarContainer = styled.div`
  padding: 15px 11px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  position: relative;
  border-right: 1px ${p => p.theme.color.paleBlue} dashed;
  border-top: 1px ${p => p.theme.color.paleBlue} dashed;
  border-bottom: 1px ${p => p.theme.color.paleBlue} dashed;
`;

const Separator = styled.div`
  height: 1px;
  background-color: ${p => p.theme.color.paleBlue};
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
`;

const PeriodName = styled.span`
  color: ${p => p.theme.color.battleshipGrey};
  font-size: 12px;
  text-align: center;
  padding: 9px;
`;

interface MoodBarProps {
  value?: number;
}

const MoodBar = styled.div<MoodBarProps>`
  background-color: ${p => p.theme.color.getBarColor(moodTypeFromValue(p.value || 0))};
  height: ${p => Math.abs(p.value || 0) * 50}%;
  border-radius: ${p => (p.value || 0 === 0 ? 2 : 0)}px;
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;
  transform: ${p => (p.value || 0) < 0 && 'rotate(180deg)'} ${p => (p.value !== 0 ? 'translateY(-50%)' : '')};
  width: 100%;
  min-height: ${p => (p.value ? '11px' : '0px')};
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const Bar = (props: BarProps) => {
  return (
    <Container>
      <BarContainer>
        <Separator />
        <MoodBar value={props.value} />
      </BarContainer>
      <PeriodName>{props.periodName}</PeriodName>
    </Container>
  );
};

const MoodChartContainer = styled.div`
  display: flex;
  height: 100%;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 2px 8px 2px 2px;
  margin-bottom: 30px;

  span {
    color: ${p => p.theme.color.battleshipGrey};
  }
`;

const Legend = () => {
  return (
    <LegendContainer>
      <span>+</span>
      <span>0</span>
      <span>-</span>
    </LegendContainer>
  );
};

const MoodText = styled.span`
  font-size: 14px;
  color: ${p => p.theme.color.battleshipGrey};
  margin-bottom: 23px;
`;

const DailyMood = (props: Props) => {
  const intl = useIntl();

  const dailyMood = props.values[0];
  const status = dailyMood && dailyMood.status;
  let sentimentString: any;

  if (dailyMood !== undefined && status && isAnalysisFailed(status)) {
    sentimentString = status && analysisStatusDescriptionMessage(status);
  } else if (dailyMood === undefined || (dailyMood && !isAnalysisAnalyzed(dailyMood.status))) {
    sentimentString = intl.formatMessage({
      id: `${messageBase}.day.empty`,
    });
  } else {
    const moodType = moodTypeFromValue(dailyMood.moodValue || 0);

    sentimentString = intl.formatMessage(
      {
        id: `${messageBase}.day`,
      },
      {
        mood: <FormattedMessage id={`${messageBase}.${moodType}`} />,
      },
    );
  }

  return <MoodText>{sentimentString}</MoodText>;
};

const PeriodMood = (props: Props) => {
  return (
    <MoodChartContainer>
      <Legend />
      <Bars>
        {props.values.map(moodValue => {
          const format: string = periodNameFormat(props.periodType);
          const periodName = moment(moodValue.date).format(format);
          return <Bar value={moodValue.moodValue} periodName={periodName} key={moodValue.date.toDateString()} />;
        })}
      </Bars>
    </MoodChartContainer>
  );
};

const periodNameFormat = (periodType: PeriodType): string => {
  switch (periodType) {
    case PeriodType.MONTH:
      return 'D';
    case PeriodType.WEEK:
      return 'ddd';
    case PeriodType.YEAR:
      return `MMM`;
    default:
      return 'MMM DD';
  }
};

const EmptyMood = () => {
  return <MoodText>empty</MoodText>;
};

const MoodChart = (props: Props) => {
  const values = props.values;
  if (values.length === 0) {
    return <EmptyMood />;
  } else if (values.length === 1) {
    return <DailyMood values={props.values} periodType={props.periodType} />;
  } else {
    return <PeriodMood values={props.values} periodType={props.periodType} />;
  }
};

export const MoodInsights = (props: Props) => {
  const intl = useIntl();
  return (
    <MoodStatsContainer
      title={intl.formatMessage({
        id: `${messageBase}.title`,
      })}
    >
      <MoodChart values={props.values} periodType={props.periodType} />
    </MoodStatsContainer>
  );
};



// WEBPACK FOOTER //
// ./src/components/MoodInsights/index.tsx